<script setup>
import { nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BillingInvoicesTable from 'dashboard/components-next/billing/BillingInvoicesTable.vue';
import BillingInvoiceDocument from 'dashboard/components-next/billing/BillingInvoiceDocument.vue';
import BillingAPI from 'dashboard/api/billing';
import { useBillingCheckout } from 'dashboard/composables/useBillingCheckout';

const { t } = useI18n();
const { payInvoice } = useBillingCheckout();

const invoices = ref([]);
const isLoading = ref(true);
const payingInvoiceId = ref(null);
const selectedInvoice = ref(null);
const dialogRef = ref(null);

const openInvoice = async invoice => {
  try {
    const { data } = await BillingAPI.getInvoice(invoice.id);
    selectedInvoice.value = data.invoice;
  } catch {
    selectedInvoice.value = invoice;
  }
  await nextTick();
  dialogRef.value?.open();
};

const closeDialog = () => {
  selectedInvoice.value = null;
};

const payInvoiceRow = async invoice => {
  payingInvoiceId.value = invoice.id;
  await payInvoice(invoice.id);
  payingInvoiceId.value = null;
};

const loadInvoices = async () => {
  isLoading.value = true;
  try {
    const { data } = await BillingAPI.getInvoices();
    invoices.value = data.invoices || [];
  } catch {
    useAlert(t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICES_LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ reload: loadInvoices });

onMounted(loadInvoices);
</script>

<template>
  <section
    class="flex flex-col gap-4 border-0 border-b border-solid border-n-weak p-5"
  >
    <h3 class="mb-0 text-base font-semibold text-n-slate-12">
      {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICES_TITLE') }}
    </h3>

    <BillingInvoicesTable
      :invoices="invoices"
      :is-loading="isLoading"
      :paying-invoice-id="payingInvoiceId"
      @view="openInvoice"
      @pay="payInvoiceRow"
    />

    <Dialog
      ref="dialogRef"
      type="edit"
      title=""
      :show-cancel-button="false"
      :show-confirm-button="false"
      width="3xl"
      overflow-y-auto
      position="top"
      @close="closeDialog"
    >
      <BillingInvoiceDocument
        v-if="selectedInvoice"
        :invoice="selectedInvoice"
        :is-paying="payingInvoiceId === selectedInvoice.id"
        @pay="payInvoiceRow"
      />
    </Dialog>
  </section>
</template>
